/**
 * GPU capability + tier detection — resolved once per session.
 *
 * The single source of truth every other GPU-platform module reads. Answers
 * three questions cheaply and synchronously: which backend can this browser
 * drive, how much should we ask of it, and does the user want motion at all.
 *
 * WebGPU *presence* (`navigator.gpu`) is detected synchronously here; whether an
 * adapter actually resolves is confirmed lazily in `webgpu-device` (that step is
 * async and may fail even when `navigator.gpu` exists). `probeWebGPU()` runs the
 * full async check when a caller needs certainty before committing to a canvas.
 */

export type GpuBackend = "webgpu" | "webgl2" | "none"
export type GpuTier = "low" | "mid" | "high"

export interface GpuCapabilities {
  /** Best backend this browser can drive, subject to async adapter confirmation. */
  backend: GpuBackend
  hasWebGPU: boolean
  hasWebGL2: boolean
  /** User asked to minimize motion — effects should show a static fallback. */
  reducedMotion: boolean
  /** Data-saver on — treat like reducedMotion for heavy effects. */
  saveData: boolean
  /** Coarse performance budget from core count + memory. */
  tier: GpuTier
}

const SERVER_DEFAULT: GpuCapabilities = {
  backend: "none",
  hasWebGPU: false,
  hasWebGL2: false,
  reducedMotion: false,
  saveData: false,
  tier: "mid",
}

let cached: GpuCapabilities | null = null

function detectWebGL2(): boolean {
  try {
    const canvas = document.createElement("canvas")
    const gl = canvas.getContext("webgl2")
    const ok = !!gl
    // Release immediately — do not hold a context just to feature-detect.
    gl?.getExtension("WEBGL_lose_context")?.loseContext()
    return ok
  } catch {
    return false
  }
}

function detectTier(): GpuTier {
  const cores = (typeof navigator !== "undefined" && navigator.hardwareConcurrency) || 4
  const mem = (typeof navigator !== "undefined" && (navigator as Navigator & { deviceMemory?: number }).deviceMemory) || 4
  if (cores <= 4 || mem <= 4) return "low"
  if (cores <= 8 || mem <= 8) return "mid"
  return "high"
}

/**
 * Synchronous, memoized capability snapshot. SSR-safe (returns a neutral
 * server default). Reflects backend *availability*, not confirmed adapters.
 */
export function getGpuCapabilities(): GpuCapabilities {
  if (cached) return cached
  if (typeof window === "undefined" || typeof navigator === "undefined") {
    return SERVER_DEFAULT
  }

  const hasWebGPU = "gpu" in navigator && !!navigator.gpu
  const hasWebGL2 = detectWebGL2()
  const reducedMotion =
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean } }).connection
  const saveData = !!conn?.saveData

  const backend: GpuBackend = hasWebGPU ? "webgpu" : hasWebGL2 ? "webgl2" : "none"

  cached = {
    backend,
    hasWebGPU,
    hasWebGL2,
    reducedMotion,
    saveData,
    tier: detectTier(),
  }
  return cached
}

/**
 * Confirms a WebGPU adapter actually resolves (the async step `navigator.gpu`
 * presence cannot guarantee). Cheap enough to call before committing a canvas.
 */
export async function probeWebGPU(): Promise<boolean> {
  const caps = getGpuCapabilities()
  if (!caps.hasWebGPU || !navigator.gpu) return false
  try {
    const adapter = await navigator.gpu.requestAdapter({ powerPreference: "high-performance" })
    return !!adapter
  } catch {
    return false
  }
}

/**
 * True when an effect should render its static fallback rather than animate:
 * reduced-motion, data-saver, or no GPU backend at all.
 */
export function shouldRenderStatic(): boolean {
  const caps = getGpuCapabilities()
  return caps.reducedMotion || caps.saveData || caps.backend === "none"
}
